import React from "react";
import { Helmet } from "react-helmet";

const PrivacyPolicy = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy | DojoTrack</title>
        <meta
          name="description"
          content="Learn how DojoTrack protects your privacy and handles personal information. Our comprehensive privacy policy covers data collection, usage, security, and your rights."
        />
        <meta
          name="keywords"
          content="dojo track privacy, martial arts software privacy, dojo management privacy, data protection, personal information"
        />
        <meta name="author" content="DojoTrack LLC" />
        <meta property="og:title" content="Privacy Policy - DojoTrack" />
        <meta
          property="og:description"
          content="Learn how DojoTrack protects your privacy and handles personal information."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://dojotrack.com/privacy" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Privacy Policy - DojoTrack" />
        <meta
          name="twitter:description"
          content="Learn how DojoTrack protects your privacy and handles personal information."
        />
        <link rel="canonical" href="https://dojotrack.com/privacy" />
      </Helmet>
      <div className="bg-white dark:bg-gray-900">
        <div className="mx-auto max-w-4xl px-6 py-16 sm:py-24 lg:px-8">
          <div className="mx-auto max-w-2xl lg:mx-0">
            <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
              Privacy Policy
            </h1>
            <p className="mt-6 text-lg leading-8 text-gray-600 dark:text-gray-400">
              Last updated: December 1st, 2025
            </p>
          </div>
          <div className="mx-auto mt-16 max-w-2xl lg:mx-0 lg:max-w-none">
            <div className="prose prose-gray dark:prose-invert max-w-none [&>h2]:text-gray-900 dark:[&>h2]:text-white [&>h3]:text-gray-800 dark:[&>h3]:text-gray-200 [&>p]:text-gray-700 dark:[&>p]:text-gray-300 [&>ul]:text-gray-700 dark:[&>ul]:text-gray-300 [&>li]:text-gray-700 dark:[&>li]:text-gray-300 [&>strong]:text-gray-900 dark:[&>strong]:text-white">
              <h2>1. Information We Collect</h2>
              
              <h3>1.1 Studio Information</h3>
              <p>
                When you create a DojoTrack account, we collect:
              </p>
              <ul>
                <li>Studio name and contact information</li>
                <li>Business address and phone number</li>
                <li>Payment information for subscription billing</li>
                <li>Account credentials and authentication data</li>
              </ul>

              <h3>1.2 Student and Lead Information</h3>
              <p>
                We collect comprehensive information about students and leads, including:
              </p>
              <ul>
                <li>Full name, email address, and phone number</li>
                <li>Date of birth and age</li>
                <li>Emergency contact information</li>
                <li>Medical information and allergies</li>
                <li>Belt rank and progression history</li>
                <li>Attendance records and class participation</li>
                <li>Payment history and billing information</li>
                <li>Student photos and profile images</li>
                <li>Notes and progress tracking data</li>
              </ul>

              <h3>1.3 Event Registration Data</h3>
              <p>
                When individuals register for events through our platform, we collect:
              </p>
              <ul>
                <li>First and last name</li>
                <li>Email address and phone number</li>
                <li>Date of birth</li>
                <li>Address information</li>
                <li>Emergency contact details</li>
                <li>Medical information and allergies</li>
                <li>Martial arts experience level</li>
                <li>Current belt rank</li>
                <li>Parent/guardian information (for minors)</li>
                <li>Payment information (if event requires payment)</li>
                <li>IP address and user agent for security</li>
                <li>Registration timestamp and submission data</li>
              </ul>

              <h3>1.4 Digital Waiver Information</h3>
              <p>
                For digital waiver submissions, we collect:
              </p>
              <ul>
                <li>Full legal name</li>
                <li>Email address and phone number</li>
                <li>Date of birth</li>
                <li>Address and contact information</li>
                <li>Emergency contact details</li>
                <li>Parent/guardian information (for minors)</li>
                <li>Electronic signature data</li>
                <li>IP address and user agent</li>
                <li>Timestamp of signature and submission</li>
                <li>Waiver acceptance and consent records</li>
              </ul>

              <h3>1.5 Staff and Instructor Information</h3>
              <p>
                For staff members and instructors, we collect:
              </p>
              <ul>
                <li>Name, email, and contact information</li>
                <li>Role and permission levels</li>
                <li>Belt rank and qualifications</li>
                <li>Class assignments and schedules</li>
              </ul>

              <h3>1.6 Phone Number Rental Service</h3>
              <p>
                When you rent a phone number through our platform for SMS messaging, we collect:
              </p>
              <ul>
                <li>Requested area code and city/location preferences</li>
                <li>The phone number assigned to your studio</li>
                <li>SMS message content sent and received through the rented number</li>
                <li>Phone numbers of message recipients (your students, leads, and contacts)</li>
                <li>Message delivery status and timestamps</li>
                <li>Usage statistics for billing purposes</li>
              </ul>

              <h3>1.7 Technical Information</h3>
              <p>
                We automatically collect:
              </p>
              <ul>
                <li>IP addresses and device information</li>
                <li>Browser type and version</li>
                <li>Operating system information</li>
                <li>Usage patterns and analytics data</li>
                <li>Error logs and performance metrics</li>
              </ul>

              <h2>2. How We Use Your Information</h2>
              
              <h3>2.1 Service Provision</h3>
              <p>
                We use collected information to:
              </p>
              <ul>
                <li>Provide and maintain our martial arts management platform</li>
                <li>Process payments and manage subscriptions</li>
                <li>Track student attendance and progress</li>
                <li>Manage class schedules and instructor assignments</li>
                <li>Generate reports and analytics for studio management</li>
                <li>Facilitate event registrations and digital waivers</li>
                <li>Send important notifications and updates</li>
              </ul>

              <h3>2.2 Communication</h3>
              <p>
                We may use your contact information to:
              </p>
              <ul>
                <li>Send account-related notifications</li>
                <li>Provide customer support</li>
                <li>Send service updates and announcements</li>
                <li>Respond to inquiries and requests</li>
              </ul>

              <h3>2.3 Legal Compliance</h3>
              <p>
                We may use your information to:
              </p>
              <ul>
                <li>Comply with applicable laws and regulations</li>
                <li>Respond to legal requests and court orders</li>
                <li>Protect our rights and property</li>
                <li>Prevent fraud and ensure security</li>
              </ul>

              <h3>2.4 SMS Messaging Service</h3>
              <p>
                When you rent a phone number through our platform, we use the collected information to:
              </p>
              <ul>
                <li>Provision and manage your dedicated SMS phone number</li>
                <li>Enable you to send and receive SMS messages with your students, leads, and contacts</li>
                <li>Process and deliver SMS messages on your behalf</li>
                <li>Track message delivery status and maintain message logs</li>
                <li>Calculate usage-based billing for SMS services</li>
                <li>Provide customer support for SMS-related issues</li>
              </ul>

              <h3>2.5 Communications and Marketing</h3>
              <p>
                We may use your contact information to send you emails and other communications for purposes including, but not limited to: customer support, updates about our platform, important notifications, onboarding materials, newsletters, promotions, marketing, and product announcements. You can opt out of receiving marketing or promotional emails at any time by following the unsubscribe instructions in those emails or by contacting us at support@dojotrack.com. Please note that you may continue to receive essential service, transactional, or account-related communications even if you opt out of marketing emails.
              </p>

              <h2>3. Information Sharing and Disclosure</h2>
              
              <h3>3.1 Mobile Phone Number Protection</h3>
              <p>
                <strong>We do not share, sell, or rent mobile phone numbers with third parties or affiliates for marketing or promotional purposes.</strong> Mobile phone numbers collected through our platform are used solely for:
              </p>
              <ul>
                <li>Providing SMS messaging services that you (the studio) initiate</li>
                <li>Facilitating communication between studios and their students/leads/contacts</li>
                <li>Essential service operations (message delivery through Twilio)</li>
                <li>Compliance with legal obligations</li>
              </ul>
              <p>
                Your mobile information remains confidential and is protected under this privacy policy. We only share mobile phone numbers with our SMS service provider (Twilio) to the extent necessary to deliver messages on your behalf.
              </p>

              <h3>3.2 Studio Access</h3>
              <p>
                Student and lead information is accessible to authorized staff members within your studio based on their permission levels.
              </p>

              <h3>3.3 Service Providers</h3>
              <p>
                We may share information with trusted third-party service providers who assist us in operating our platform. These service providers are contractually obligated to use your information only for providing services to us and not for their own purposes:
              </p>
              <ul>
                <li>Payment processing (Stripe) - for subscription and payment management</li>
                <li>Email communications (Gmail) - for sending emails on your behalf</li>
                <li>SMS communications (Twilio) - for sending and receiving text messages through rented phone numbers</li>
                <li>Data storage and hosting (Supabase) - for secure data storage</li>
                <li>Customer support services</li>
              </ul>
              <p>
                <strong>Important:</strong> Twilio, our SMS service provider, processes phone numbers and message content solely to deliver SMS messages on your behalf. They do not use this information for marketing purposes or share it with other third parties.
              </p>

              <h3>3.4 Legal Requirements</h3>
              <p>
                We may disclose your information if required by law or to protect our rights and safety.
              </p>

              <h2>4. Data Security</h2>
              <p>
                We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. This includes:
              </p>
              <ul>
                <li>Encryption of data in transit and at rest</li>
                <li>Regular security assessments and updates</li>
                <li>Access controls and authentication</li>
                <li>Secure data centers and infrastructure</li>
              </ul>

              <h2>5. Data Retention</h2>
              <p>
                We retain your information for as long as necessary to provide our services and comply with legal obligations. Our retention framework includes:
              </p>
              <ul>
                <li>Account and student data: Retained while your studio account is active (subject to inactivity deletion policies below)</li>
                <li>Event registrations: 7 years for legal compliance</li>
                <li>Digital waivers: 7 years for legal compliance</li>
                <li>Payment records: 7 years for tax and legal purposes</li>
              </ul>
              <p>
                In the event of business closure, bankruptcy, or cessation of operations, we will provide advance notice to allow studios to export their data. Data that must be retained for legal compliance purposes may be transferred to a successor entity or destroyed in accordance with applicable laws and regulations.
              </p>

              <h3>5.1 Additional Data Retention and Deletion Practices</h3>
              <ul>
                <li>We will delete your account and all associated data if your account remains inactive (no login or indication of interest in account recovery) for six (6) months. Once deleted, your account and data are not recoverable.</li>
                <li>When you request account deletion, your data will be permanently deleted after a 30-day grace period. During this grace period, you can cancel your deletion request and reactivate your account.</li>
                <li>Attendance records are deleted three (3) months after their creation.</li>
                <li>Registration image data from events is deleted three (3) months after the event date.</li>
                <li>Logs of sent emails are deleted three (3) months after sending.</li>
                <li>Logs of scheduled emails are deleted three (3) months after they were scheduled, regardless of send status.</li>
                <li>Logs of scheduled SMS messages are deleted three (3) months after they were scheduled, regardless of send status.</li>
                <li>SMS message content and delivery logs are retained for three (3) months for operational and support purposes, then permanently deleted.</li>
                <li>Rented phone number information is retained while your SMS subscription is active and for seven (7) years after cancellation for billing and legal compliance purposes.</li>
                <li>Appointment data is deleted three (3) months after the appointment date.</li>
                <li>Notification records are deleted three (3) months after their creation.</li>
              </ul>

              <h2>6. Your Rights</h2>
              <p>
                Depending on your location, you may have the following rights:
              </p>
              <ul>
                <li>Access: Request a copy of your personal information</li>
                <li>Correction: Request correction of inaccurate information</li>
                <li>Deletion: Request deletion of your personal information</li>
                <li>Portability: Request transfer of your data</li>
                <li>Objection: Object to certain processing activities</li>
                <li>Restriction: Request restriction of processing</li>
              </ul>

              <h3>6.1 SMS Communication Rights</h3>
              <p>
                If you receive SMS messages from a studio using DojoTrack:
              </p>
              <ul>
                <li>You have the right to opt out of SMS communications at any time by replying "STOP" to any message. The studio is responsible for honoring your opt-out request and ceasing SMS communications to your number.</li>
                <li>Message and data rates may apply based on your mobile carrier's plan</li>
                <li>The studio (not DojoTrack) is responsible for obtaining proper consent before sending you SMS messages</li>
                <li>You can contact the studio directly to update your mobile phone number or communication preferences</li>
              </ul>
              <p>
                For studios using our SMS rental service: You are responsible for obtaining proper consent from recipients before sending SMS messages, managing opt-out requests, and complying with applicable telecommunications laws and regulations, including TCPA (Telephone Consumer Protection Act) and CTIA guidelines.
              </p>

              <h3>6.2 Data Export Capabilities</h3>
              <p>
                We provide data export functionality for key data types to support your data portability rights and business continuity needs:
              </p>
              <ul>
                <li>Event registration data can be exported in machine-readable formats</li>
                <li>Digital waiver submissions and associated data can be exported for record-keeping</li>
                <li>Student and lead information can be exported from your studio account</li>
                <li>SMS message logs can be exported for your records</li>
              </ul>

              <h2>7. Children's Privacy</h2>
              <p>
                DojoTrack serves as a platform for martial arts studios to manage their student information. When studios enter information about children under 13 into our platform, it is the studio's responsibility to:
              </p>
              <ul>
                <li>Obtain proper parental consent before collecting and entering children's information</li>
                <li>Ensure compliance with applicable children's privacy laws (such as COPPA)</li>
                <li>Provide parents with the ability to review, modify, or delete their child's information</li>
                <li>Handle children's data in accordance with their own privacy policies and legal obligations</li>
              </ul>
              <p>
                DojoTrack provides tools and functionality to support studios in managing this data securely, but the studios are the data controllers responsible for ensuring proper consent and compliance with children's privacy regulations.
              </p>

              <h2>8. International Data Transfers</h2>
              <p>
                Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place for such transfers.
              </p>

              <h2>9. Cookies and Tracking</h2>
              <p>
                We use cookies and similar technologies to enhance your experience and analyze usage patterns. You can control cookie settings through your browser preferences.
              </p>

              <h2>10. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on our website and updating the "Last updated" date.
              </p>

              <h2>11. Contact Us</h2>
              <p>
                If you have questions about this Privacy Policy or our data practices, please contact us at:
              </p>
              <p>
                support@dojotrack.com
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;
